import { P2GChip } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { useCallback, useMemo } from 'react';
import { ServiceTabType } from './ServiceInfo';
import { DeliveredDutyPaidExtra } from '../../ExtrasHelper';
import { DropLocation } from '~/lib/api/smartsend/types';
import { useDistance } from '@parcel2go/npm-p2g-app';

interface props {
  service: { Tags: string[]; CourierProductCode: string; DeliveryType: string };
  setTab?: (tab: ServiceTabType) => void;
  availableExtras: string[];
  closestPickupLocation?: DropLocation;
}

const TAGS_TO_SHOW = ['SmartSendExclusive', 'LetterBox', 'PrintInStoreOnly'];
const BOX_SIZES = ['Parcel', 'Standard Letter', 'Large Letter'];

const ServiceTags: React.FC<props> = (props) => {
  const { toDistance } = useDistance();

  const setTab = (tab: ServiceTabType) => {
    if (props.setTab) {
      props.setTab(tab);
    }
  };

  const visibleTags = useMemo(() => {
    const output = props.service.Tags.filter((t) =>
      TAGS_TO_SHOW.map((tag) => tag.toLowerCase()).includes(t.toLowerCase())
    );

    // If the service allows multiple options, dont show a specific restriction.
    const size = props.service.Tags.filter((t) => BOX_SIZES.includes(t));
    size.length === 1 && output.push(`${size[0]} Service`);

    // Add ProductCode as a 'Tag'
    props.service.CourierProductCode && output.unshift(props.service.CourierProductCode.toUpperCase());

    // Locker or Shop Pickup
    if (props.service.DeliveryType === 'Locker') {
      output.push('LockerDelivery');
    } else if (props.service.DeliveryType === 'Shop') {
      output.push('ShopDelivery');
    }

    return output;
  }, [props.service]);

  const hasDdp = useMemo(() => {
    // Order API already checks if DDP can be bough so no need to check AvailableExtras.
    if (props.service?.Tags?.includes('QuoteApi') && props.service.Tags.includes('ddp')) {
      return true;
    }

    return props.availableExtras?.includes(DeliveredDutyPaidExtra);
  }, [props.availableExtras, props.service]);

  const renderLabel = useCallback(
    (tag: string) => {
      switch (tag.toLowerCase()) {
        case 'ui:smartsendexclusive':
        case 'smartsendexclusive':
          return (
            <>
              Exclusive Smart{' '}
              <strong>
                <i>Send</i>
              </strong>{' '}
              Offer
            </>
          );
        case 'printinstoreonly':
          return <>Print In Store Only</>;

        case 'lockerdelivery':
          return (
            <>
              Recipient Collects from Locker{' '}
              {props.closestPickupLocation?.Distance && <>({toDistance(props.closestPickupLocation?.Distance)})</>}
            </>
          );

        case 'shopdelivery':
          return (
            <>
              Recipient Collects from Shop{' '}
              {props.closestPickupLocation?.Distance && <>({toDistance(props.closestPickupLocation?.Distance)})</>}
            </>
          );
        default:
          return tag;
      }
    },
    [props.closestPickupLocation, toDistance]
  );

  return (
    <P2GGrid container spacing={1}>
      {hasDdp && (
        <P2GGrid item>
          <P2GChip onClick={() => setTab('CUSTOMS')} size="small" label="Duty Paid Service Available" color="primary" />
        </P2GGrid>
      )}
      {visibleTags.map((t, i) => (
        <P2GGrid item key={t}>
          <P2GChip size="small" label={renderLabel(t) as any} color={i === 0 ? 'primary' : 'info'} />
        </P2GGrid>
      ))}
    </P2GGrid>
  );
};
export default ServiceTags;
